import { Box, Image, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterSearch } from '../../../components/FilterBar'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { formatDateTime, useHasRole, useAppToast } from '../../../helpers'
import { IOurStudent } from '../../../interfaces/Testimonials/ITestimonials'
import { TestimonialsService } from '../../../services/Testimonials/TestimonialsService'
import { TestimonialsEditModal } from './TestimonialsEditModal'
import { TestimonialsFormModal } from './TestimonialsFormModal'

export default function OurStudentsPage() {
  const service = new TestimonialsService()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const permissions = {
    addStudent: hasRole('add_student'),
    editStudent: hasRole('edit_student'),
    deleteStudent: hasRole('delete_student')
  }

  const [records, setRecords] = useState<IOurStudent[]>([])
  const [filters, setFilters] = useState({ page_size: DEFAULT_PAGE_SIZE })
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const [search, setSearch] = useState('')
  const [filteredRecords, setFilteredRecords] = useState<IOurStudent[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [recordEditingId, setRecordEditingId] = useState<number | null>(null)

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [recordDeletingId, setRecordDeletingId] = useState<number | null>(null)

  const [knowledgeAreas, setKnowledgeAreas] = useState<{ id: number; name: string }[]>([])

  const fetchStudents = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const pageSize = filters.page_size
      if (isNaN(pageSize) || pageSize <= 0) return

      const response = await service.list({
        search: '',
        page,
        page_size: pageSize
      })

      setRecords(response.data)

      const pages = Math.ceil(response.recordsTotal / pageSize)
      setTotalPages(pages > 0 ? pages : 1)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os depoimentos', error)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    fetchStudents()
  }, [page, filters])

  useEffect(() => {
    service
      .listKnowledgeArea()
      .then(setKnowledgeAreas)
      .catch(() => {
        toast.error('Não foi possível carregar as áreas de conhecimento')
      })
  }, [])

  useEffect(() => {
    setFilteredRecords(records.filter((student) => student.name.toLowerCase().includes(search.toLowerCase())))
  }, [search, records])

  function openEditModal(id: number) {
    setRecordEditingId(id)
    setIsEditModalOpen(true)
  }

  function closeEditModal() {
    setIsEditModalOpen(false)
    setRecordEditingId(null)
  }

  function closeFormModal() {
    setIsFormModalOpen(false)
  }

  function openDeleteDialog(id: number) {
    setRecordDeletingId(id)
    setIsDeleteOpen(true)
  }

  function closeDeleteDialog() {
    setIsDeleteOpen(false)
    setRecordDeletingId(null)
  }

  const handleDelete = async () => {
    if (!recordDeletingId) return

    try {
      await service.delete(recordDeletingId)

      toast.success('Estudante excluído')

      setRecords((prev) => prev.filter((student) => student.id !== recordDeletingId))
    } catch (error) {
      toast.error('Erro ao excluir estudante', error)
    } finally {
      closeDeleteDialog()
    }
  }

  return (
    <>
      <PageTitle title="Depoimentos de alunos" description="Depoimentos publicados no site da ABRA" />

      <FilterBar onClear={() => setSearch('')} isDirty={search !== ''}>
        <FilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Buscar por nome"
          // Filtragem local: nada de esperar meio segundo para filtrar uma lista já carregada.
          delay={0}
        />
      </FilterBar>

      <TableContainer padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Imagem</Th>
              <Th>Nome</Th>
              <Th>Área de Conhecimento</Th>
              <Th>Depoimento</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={7} />
            ) : loadError ? (
              <TableErrorRow colSpan={7} entity="depoimentos" onRetry={fetchStudents} />
            ) : filteredRecords.length === 0 ? (
              <TableEmptyRow colSpan={7} entity="depoimento" />
            ) : (
              filteredRecords
                .sort((a, b) => a.id - b.id)
                .map((student) => (
                  <Tr key={student.id}>
                    <Td>{student.id}</Td>
                    <Td>
                      <Box boxSize="38px" overflow="hidden" borderRadius="md">
                        <Image src={student.image} alt={student.name} objectFit="cover" boxSize="100%" />
                      </Box>
                    </Td>
                    <Td>{student.name}</Td>
                    <Td>{student.knowledge_area.name}</Td>
                    <Td>
                      <Tooltip label={student.description}>
                        <Text noOfLines={1} maxW="200px">
                          {student.description}
                        </Text>
                      </Tooltip>
                    </Td>

                    <Td>
                      <EmptyCell value={formatDateTime(student.created_at)} />
                    </Td>
                    <Td>
                      {permissions.editStudent && (
                        <ActionButton action="edit" onClick={() => openEditModal(student.id)} />
                      )}

                      {permissions.deleteStudent && (
                        <ActionButton action="delete" onClick={() => openDeleteDialog(student.id)} />
                      )}
                    </Td>
                  </Tr>
                ))
            )}
          </Tbody>
        </Table>

        <ConfirmDialog
          isOpen={isDeleteOpen}
          onClose={closeDeleteDialog}
          onConfirm={handleDelete}
          action="delete"
          entity="estudante"
          name={recordDeletingId}
        />
      </TableContainer>

      <TestimonialsEditModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        id={recordEditingId ?? 0}
        knowledgeAreas={knowledgeAreas}
        onUpdated={fetchStudents}
      />

      <TestimonialsFormModal
        isOpen={isFormModalOpen}
        onClose={closeFormModal}
        knowledgeAreas={knowledgeAreas}
        onSuccess={() => {
          fetchStudents()
          setPage(1)
        }}
      />

      {permissions.addStudent && (
        <ActionFab
          label="Adicionar manualmente"
          onClick={() => {
            setIsFormModalOpen(true)
          }}
        />
      )}

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(p) => setPage(p)}
        pageSize={filters.page_size}
        onPageSizeChange={(pageSize) => {
          setFilters({ page_size: pageSize })
          setPage(1)
        }}
      />
    </>
  )
}
